
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';

const TechSpecs = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const specs = [
    {
      icon: 'ri-shield-check-line',
      title: 'Gıda Onaylı',
      value: 'FDA ve AB Standartları',
      desc: 'Tüm ürünlerimiz gıda temasına uygun, sertifikalı hammaddelerle üretilir.',
      color: 'from-green/20 to-teal/20',
    },
    {
      icon: 'ri-recycle-line',
      title: '100% Geri Dönüşüm',
      value: 'Çevre Dostu Üretim',
      desc: 'Tamamen geri dönüştürülebilir malzemeler ile sürdürülebilir ambalaj.',
      color: 'from-teal/20 to-green/20',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Isı Direnci',
      value: '-40°C ile +60°C',
      desc: 'Geniş sıcaklık aralığında güvenle kullanılabilir dayanıklı yapı.',
      color: 'from-navy/15 to-teal/15',
    },
    {
      icon: 'ri-box-3-line',
      title: 'Özel Üretim',
      value: 'İstenilen Hacim & Tasarım',
      desc: 'Müşteri taleplerine özel hacim, renk ve tasarım seçenekleri.',
      color: 'from-green/15 to-navy/15',
    },
    {
      icon: 'ri-eye-line',
      title: 'Yüksek Berraklık',
      value: 'Kristal Şeffaflık',
      desc: 'Ürün görünürlüğünü artıran üstün optik berraklık.',
      color: 'from-teal/15 to-navy/15',
    },
    {
      icon: 'ri-weight-line',
      title: 'Hafif & Dayanıklı',
      value: 'Optimize Ağırlık',
      desc: 'Minimum ağırlıkta maksimum dayanıklılık sağlayan tasarım.',
      color: 'from-navy/15 to-green/15',
    },
  ];

  return (
    <section
      ref={ref}
      id="teknik"
      className="py-16 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50/80 to-white relative overflow-hidden"
    >
      {/* Background Decoration */}
      <motion.div
        animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-teal/5 to-green/3 rounded-full blur-3xl pointer-events-none"
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <motion.span
            initial={{ opacity: 0, letterSpacing: '0.05em' }}
            animate={inView ? { opacity: 1, letterSpacing: '0.2em' } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-teal text-xs sm:text-sm font-semibold uppercase inline-block"
          >
            /TEKNİK ÖZELLİKLER
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
            Ürün Özellikleri
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Tüm ürünlerimiz en yüksek kalite standartlarında üretilmektedir
          </p>
        </motion.div>

        {/* Specs Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {specs.map((spec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60, rotateX: 8 }}
              animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="group relative"
            >
              <div
                className={`relative bg-white p-6 sm:p-8 rounded-2xl border transition-all duration-500 overflow-hidden cursor-pointer ${
                  hoveredIndex === index
                    ? 'border-teal/30 shadow-xl -translate-y-2'
                    : 'border-gray-100 shadow-md hover:shadow-lg'
                }`}
              >
                {/* Hover Background */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${spec.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />

                {/* Shimmer */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <motion.div
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1.5 }}
                    className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                  />
                </div>

                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 6 }}
                    transition={{ duration: 0.4 }}
                    className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl text-white mb-5 shadow-md"
                  >
                    <i className={`${spec.icon} text-2xl`} />
                  </motion.div>

                  <h3 className="text-lg sm:text-xl font-bold text-navy mb-2 group-hover:text-teal transition-colors duration-300">
                    {spec.title}
                  </h3>
                  <div className="text-green text-sm font-semibold mb-3">{spec.value}</div>
                  <p className="text-gray-500 text-sm leading-relaxed">{spec.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechSpecs;
